import { ActionButton } from "@/components/defi/ActionButton";
import { AnimatedLTVBar } from "@/components/defi/AnimatedLTVBar";
import { ConfirmSheet } from "@/components/defi/ConfirmSheet";
import type { UpdateStage } from "@/components/defi/UpdateStageLoader";
import { TokenAmount, TokenLabel } from "@/components/brand/TokenAmount";
import { VaultStatusPill } from "@/components/defi/VaultStatusPill";
import type { CollateralKey } from "@/data/fixtures";
import { useAuth } from "@/lib/auth";
import { ensureProtocolUser } from "@/lib/ensure-user";
import {
	parsePositiveAmount,
	validateAmountWithinBalance,
	validateAmountWithinMax,
} from "@/lib/form-validation";
import { getIcConfig } from "@/lib/network";
import { ROUTES } from "@/lib/routes";
import { formatNum } from "@/lib/utils";
import {
	depositCollateral,
	repayUsdm,
	withdrawCollateral,
} from "@motokomaker/ic-client";
import {
	ArrowDownCircle,
	ArrowUpCircle,
	CircleDollarSign,
	Coins,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

export type PortfolioVault = {
	id: bigint;
	collateral: CollateralKey;
	/** Token units of primary collateral in the vault */
	collateralTokenBal: number;
	collateralUsd: number;
	debtUsdm: number;
	ltv: number;
	healthFactor: number;
	status: "Active" | "AtRisk";
};

type ModalMode = "deposit" | "withdraw" | "repay" | null;

function riskRec(vault: PortfolioVault): string {
	if (vault.status === "AtRisk" || vault.ltv > 0.85) {
		return "At risk — repay USDM or add collateral soon.";
	}
	if (vault.ltv > 0.75) return "Elevated LTV — consider paying down debt.";
	return "Healthy — room to borrow more if needed.";
}

export function PortfolioVaultCard({
	vault,
	onUpdated,
}: {
	vault: PortfolioVault;
	onUpdated?: () => void;
}) {
	const {
		balances,
		refreshBalances,
		ensureProtocolActor,
		isAuthenticated,
		login,
	} = useAuth();
	const qc = useQueryClient();
	const [mode, setMode] = useState<ModalMode>(null);
	const [amount, setAmount] = useState("");
	const [pending, setPending] = useState(false);
	const [success, setSuccess] = useState(false);
	const [stage, setStage] = useState<UpdateStage>("idle");

	const atRisk = vault.status === "AtRisk" || vault.ltv > 0.85;
	const highLtv = vault.ltv > 0.75;

	/** Rough free collateral cap: keep ~150% CR when debt > 0 (protocol is authoritative). */
	const maxWithdrawToken = useMemo(() => {
		if (vault.collateralTokenBal <= 0) return 0;
		if (vault.debtUsdm <= 0 || vault.collateralUsd <= 0) {
			return vault.collateralTokenBal;
		}
		const minColUsd = vault.debtUsdm * 1.5;
		const freeUsd = Math.max(0, vault.collateralUsd - minColUsd);
		const freeFrac = freeUsd / vault.collateralUsd;
		return Math.min(
			vault.collateralTokenBal,
			vault.collateralTokenBal * freeFrac,
		);
	}, [vault]);

	const amountError = useMemo(() => {
		const parsed = parsePositiveAmount(amount || "0");
		if (!amount) return undefined;
		if (!parsed.ok) return parsed.error;
		if (mode === "deposit") {
			const bal = balances[vault.collateral as keyof typeof balances] ?? 0;
			return validateAmountWithinBalance(
				parsed.amount,
				Number(bal),
				vault.collateral,
			);
		}
		if (mode === "repay") {
			return (
				validateAmountWithinBalance(parsed.amount, balances.USDM, "USDM") ??
				validateAmountWithinMax(parsed.amount, vault.debtUsdm, "USDM")
			);
		}
		if (mode === "withdraw") {
			return validateAmountWithinMax(
				parsed.amount,
				maxWithdrawToken,
				vault.collateral,
			);
		}
		return undefined;
	}, [amount, mode, balances, vault, maxWithdrawToken]);

	const title =
		mode === "deposit"
			? "Deposit collateral"
			: mode === "withdraw"
				? "Withdraw collateral"
				: mode === "repay"
					? "Pay loan"
					: "";

	function openMode(next: ModalMode) {
		if (!isAuthenticated) {
			void login();
			toast.message("Connect Internet Identity to manage vaults");
			return;
		}
		setMode(next);
		setAmount("");
		setStage("idle");
		setSuccess(false);
	}

	async function confirm() {
		if (amountError || !amount || !mode) return;
		const parsed = parsePositiveAmount(amount);
		if (!parsed.ok) {
			toast.error(parsed.error);
			return;
		}
		setPending(true);
		setStage("signing");
		try {
			const pair = await ensureProtocolActor();
			if (!pair) {
				toast.error("Live protocol required — check canister env and Connect");
				setStage("idle");
				return;
			}
			await ensureProtocolUser(pair.actor);
			const config = getIcConfig();
			setStage("submitting");
			if (mode === "deposit") {
				await depositCollateral(pair.actor, pair.agent, config, {
					vaultId: vault.id,
					collateralType: vault.collateral,
					amount: parsed.amount,
				});
			} else if (mode === "withdraw") {
				await withdrawCollateral(
					pair.actor,
					vault.id,
					vault.collateral,
					parsed.amount,
				);
			} else if (mode === "repay") {
				await repayUsdm(
					pair.actor,
					pair.agent,
					config,
					vault.id,
					parsed.amount,
				);
			}
			setStage("confirmed");
			setSuccess(true);
			toast.success(`${title} confirmed on-chain`);
			void qc.invalidateQueries({ queryKey: ["v2", "userVaults"] });
			void qc.invalidateQueries({ queryKey: ["v2", "vault"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "vaults"] });
			void qc.invalidateQueries({ queryKey: ["v2", "wallet", "activity"] });
			void qc.invalidateQueries({ queryKey: ["v2", "certifiedAnalytics"] });
			void refreshBalances();
			onUpdated?.();
			setTimeout(() => {
				setMode(null);
				setAmount("");
				setStage("idle");
				setSuccess(false);
			}, 900);
		} catch (err) {
			setStage("idle");
			toast.error(err instanceof Error ? err.message : "Update failed");
		} finally {
			setPending(false);
		}
	}

	return (
		<div className="glass-panel space-y-4 p-4">
			<div className="flex items-start justify-between gap-2">
				<div>
					<p className="font-mono-amount text-sm text-muted-foreground">
						Vault #{String(vault.id)}
					</p>
					<div className="mt-1 flex items-center gap-2">
						<TokenLabel id={vault.collateral} size="sm" />
						<VaultStatusPill status={vault.status} />
					</div>
				</div>
				<div className="text-right">
					<p className="text-[10px] uppercase text-muted-foreground">Debt</p>
					<TokenAmount id="USDM" value={vault.debtUsdm} decimals={0} size="sm" />
				</div>
			</div>

			<AnimatedLTVBar
				ltv={vault.ltv * 100}
				healthFactor={vault.healthFactor}
			/>

			<p className="text-xs text-muted-foreground">
				Coll ${formatNum(vault.collateralUsd, 0)} · HF{" "}
				{Number.isFinite(vault.healthFactor)
					? vault.healthFactor.toFixed(2)
					: "∞"}
				× · LTV {(vault.ltv * 100).toFixed(0)}%
			</p>
			<p className="text-xs text-foreground/80">{riskRec(vault)}</p>

			{atRisk ? (
				<ActionButton
					variant="destructive"
					onClick={() => openMode("repay")}
				>
					<CircleDollarSign className="size-4" />
					Pay Loan
				</ActionButton>
			) : highLtv ? (
				<ActionButton
					variant="primary"
					onClick={() => openMode("repay")}
				>
					<CircleDollarSign className="size-4" />
					Pay Loan
				</ActionButton>
			) : (
				<Link
					to={ROUTES.borrow}
					className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-brand-gradient text-sm font-semibold text-primary-foreground shadow-neon"
				>
					<Coins className="size-4" />
					Borrow more
				</Link>
			)}

			<div className="grid grid-cols-3 gap-2">
				<ActionButton
					variant="secondary"
					layout="stack"
					className="text-xs"
					onClick={() => openMode("deposit")}
				>
					<ArrowDownCircle className="size-4" />
					Deposit
				</ActionButton>
				<ActionButton
					variant="secondary"
					layout="stack"
					className="text-xs"
					onClick={() => openMode("withdraw")}
				>
					<ArrowUpCircle className="size-4" />
					Withdraw
				</ActionButton>
				<ActionButton
					variant="secondary"
					layout="stack"
					className="text-xs"
					success={success && mode === "repay"}
					onClick={() => openMode("repay")}
				>
					<CircleDollarSign className="size-4" />
					Pay Loan
				</ActionButton>
			</div>

			<p className="text-center text-[11px] text-muted-foreground">
				<Link
					to={ROUTES.vaultDetail(vault.id)}
					className="font-semibold text-primary hover:underline"
				>
					Full vault detail →
				</Link>
			</p>

			<ConfirmSheet
				open={mode != null}
				onOpenChange={(o) => {
					if (!o && !pending) {
						setMode(null);
						setStage("idle");
					}
				}}
				title={title}
				description={
					mode === "deposit"
						? `Deposit ${vault.collateral} into vault #${String(vault.id)}. First the token is approved (ICRC), then the protocol records the deposit.`
						: mode === "withdraw"
							? `Withdraw free ${vault.collateral} from vault #${String(vault.id)} only if health stays above liquidation.`
							: `Pay down USDM debt on vault #${String(vault.id)}. Approve USDM, then repay.`
				}
				confirmLabel={
					mode === "repay"
						? "Pay Loan"
						: mode === "deposit"
							? "Deposit"
							: "Withdraw"
				}
				destructive={mode === "repay" && atRisk}
				onConfirm={() => void confirm()}
				isPending={pending}
				confirmDisabled={!!amountError || !amount}
				stage={stage}
			>
				<div className="rounded-2xl border border-border/50 bg-background/50 p-4">
					<p className="text-[10px] uppercase text-muted-foreground">Vault</p>
					<div className="mt-1 flex items-center gap-2">
						<span className="font-mono-amount text-sm font-semibold">
							#{String(vault.id)}
						</span>
						<TokenLabel id={vault.collateral} size="sm" />
					</div>
					<p className="mt-2 text-xs text-muted-foreground">
						Debt{" "}
						<span className="font-mono-amount text-foreground">
							{formatNum(vault.debtUsdm, 0)} USDM
						</span>
						{" · "}
						LTV {(vault.ltv * 100).toFixed(0)}% · HF{" "}
						{Number.isFinite(vault.healthFactor)
							? vault.healthFactor.toFixed(2)
							: "∞"}
					</p>
					{mode === "withdraw" && (
						<p className="mt-1 text-xs text-muted-foreground">
							Max free ~{" "}
							<span className="font-mono-amount text-foreground">
								{formatNum(maxWithdrawToken, 4)} {vault.collateral}
							</span>
						</p>
					)}
				</div>

				<label className="block">
					<span className="text-xs font-medium text-muted-foreground">
						Amount ({mode === "repay" ? "USDM" : vault.collateral})
					</span>
					<input
						value={amount}
						onChange={(e) => setAmount(e.target.value)}
						inputMode="decimal"
						autoFocus
						className="input-glow mt-2 h-14 w-full rounded-2xl border border-border/60 bg-background/60 px-4 font-mono-amount text-lg text-foreground"
						placeholder="0.00"
					/>
				</label>
				{amountError && (
					<p className="text-sm text-magenta" role="alert">
						{amountError}
					</p>
				)}
				{!amountError && amount && mode === "repay" && (
					<p className="text-sm text-muted-foreground">
						After repay, debt falls by up to{" "}
						<span className="font-mono-amount text-foreground">
							{formatNum(Number(amount) || 0, 2)} USDM
						</span>
						.
					</p>
				)}
			</ConfirmSheet>
		</div>
	);
}
